import { expressionFunctions } from '@lmt/expressions/src/expressions/helpers/expression-functions';
import { PatientDto, QueryFactory } from '@lmt/shared-types';
import { DataType, HL7TargetDefinition } from '@lmt/shared-types';

import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { MonacoExpressionBuilder } from '@lmt/unicorn-shared/app/common/Monaco/MonacoExpressionBuilder';
import {
  useDictionaryFieldsFilterFunc,
  useFieldNamesFilterFunc,
} from '@lmt/unicorn-shared/app/components/hl7/mappings/common/common';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { useDataGridExportHandler } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { HL7MessageSegments } from '@lmt/unicorn-shared/data/messageSegmentNames';
import { HL7MessageSubfields } from '@lmt/unicorn-shared/data/messageSubfields';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  useDataGrid,
  DXG,
  DataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { useEventCallback } from '@mui/material';
import { isEmpty } from '@lmt/unicorn-shared/app/components/measurements/copy-datablock-field/common';
import {
  allowOnlySingleNewRow,
  getAllRows,
  useCachedDataSourceWhenEditing,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';
import { readFileAsExcel } from '@lmt/unicorn-shared/app/helpers/file-helper';
import loader from '@lmt/unicorn-shared/src/app/animations/loader.gif';

export type ExpressionBuilderField = {
  value: string;
  caption: string;
  dataType: DataType;
  score?: number;
};

type Props = {
  mappingId: number;
};
type Row = {
  id: string;
} & HL7TargetDefinition;

type RowData = {
  data: Row;
  key: string;
  removed: boolean;
};

type TableRow = {
  row: RowData;
};

const nameof = nameofFactory<HL7TargetDefinition>();
const nameOfPatient = nameofFactory<PatientDto>();

const tables = ['Patient', 'Case', 'Visit'];

export const MappingTable: FC<Props> = (props) => {
  const i18n = useScopedTranslation('hl7.mapping');
  useDataGrid();
  const dataGridRef = useRef<DataGrid<Row, string>>(null);

  const {
    data: activeMapping,
    isLoading,
    refetch: refetch,
  } = QueryFactory.HL7MappingQuery.useGetSingleQuery(props.mappingId);

  const { data: queryFields } =
    QueryFactory.HL7MappingQuery.useGetFieldsQuery();

  const tableNames = useMemo(() => {
    if (!queryFields?.length) {
      return [];
    }
    // unique table names
    const set = new Set(
      queryFields
        .filter((x) => tables.includes(x.tableName) || !!x.dataBlockTypeId)
        .map((x) => x.tableName),
    );
    return [...set.values()];
  }, [queryFields]);

  const mappingRules = useMemo(
    () => activeMapping?.rules.map((x) => ({ ...x, id: createId() })),
    [activeMapping],
  );
  const dataSource = useCachedDataSourceWhenEditing(dataGridRef, mappingRules);

  const modals = useModal();

  const fieldNamesFilterFunc = useFieldNamesFilterFunc(queryFields, tableNames);
  const dictionaryFieldsFilterFunc = useDictionaryFieldsFilterFunc(queryFields);

  const validateExpression = useCallback(async (data: any) => {
    try {
      await QueryFactory.HL7MappingClient.validate(data.value);
    } catch (e: any) {
      if (e.status == 403) return;
      throw e;
    }
  }, []);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    setIsExcelLoading(true);

    try {
      const values = await readFileAsExcel(file);

      const dataGrid = dataGridRef.current!.instance();
      for (let i = 2; i < values.length; i++) {
        const row = values[i];
        try {
          const newRow = await mapExcelRowToDataGrid<Row>(dataGrid, row);
          onFieldNameChange(newRow, newRow.fieldName, newRow); // to fill dataBlockTypeId if needed
          newRow.id = createId();
          const initNewRow = (e: any) => {
            Object.assign(e.data, newRow);
          };
          dataGrid.on('initNewRow', initNewRow);
          await dataGrid.addRow();
          dataGrid.off('initNewRow', initNewRow);
        } catch (e) {
          await modals.showError({
            text: `Error parsing excel row ${i}: ${convertToErrorString(e)}`,
          });
          return;
        }
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }

  const onActivationChange = useCallback(
    async (row: TableRow, isDeactivated: boolean) => {
      try {
        const data = row.row.data!;
        await QueryFactory.HL7MappingClient.patch(props.mappingId, {
          rules: activeMapping?.rules.map((x) =>
            x.fieldName === data.fieldName &&
            x.table === data.table &&
            x.dictionaryField == data.dictionaryField
              ? { ...x, isDeleted: isDeactivated }
              : x,
          ),
        });
        await refetch();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
    [activeMapping, mappingRules],
  );
  const onRowActivate = useCallback(
    (row: TableRow) => {
      onActivationChange(row, false).catch((e) =>
        console.error('onRowActivate', e),
      );
    },
    [onActivationChange],
  );
  const onRowDeactivate = useCallback(
    (row: TableRow) => {
      onActivationChange(row, true).catch((e) =>
        console.error('onRowDeactivate', e),
      );
    },
    [onActivationChange],
  );

  const onExportHandler = useDataGridExportHandler({
    fileName: 'HL7 mappings',
  });

  const onTableChange = useEventCallback((data: Row, value: string | null) => {
    data.fieldName = '';
    data.dictionaryField = null;
    data.table = value ?? '';
  });

  const onDictionaryFieldChange = useEventCallback(
    (data: Row, value: string | null) => {
      data.fieldName = '';
      data.dictionaryField = value;
    },
  );

  const onFieldNameChange = useEventCallback(
    (data: Row, value: string, existingRow: Row) => {
      data.fieldName = value;
      const queryField = queryFields?.find(
        (x) => x.fieldName === value && x.tableName === existingRow.table,
      );
      if (!queryField?.dataBlockTypeId) {
        return;
      }
      data.dataBlockTypeId = queryField.dataBlockTypeId;
      data.dataBlockVariant = queryField.dataBlockVariant;
    },
  );

  return (
    <Loading
      loading={isLoading || tableNames.length === 0 || isExcelLoading}
      title={isExcelLoading ? i18n.t('loading_excel') : i18n.t('loading')}
    >
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
      />
      {tableNames.length > 0 && (
        <AutoSizedTableWrapper>
          <DXG.DataGrid
            ref={dataGridRef}
            export={{
              enabled: true,
            }}
            onExporting={onExportHandler}
            onToolbarPreparing={(e) => {
              e.toolbarOptions!.items!.push({
                location: 'after',
                widget: 'dxButton',
                options: {
                  width: 136,
                  text: i18n.t('import_button'),
                  onClick: () => inputFileRef.current!.click(),
                },
              });
              allowOnlySingleNewRow(e);
            }}
            loadPanel={{
              enabled: true,
              indicatorOptions: { src: loader },
            }}
            onSaving={async (ev) => {
              const allRows = getAllRows(ev);

              ev.promise = (async () => {
                dataGridRef.current?.instance().beginCustomLoading('');
                try {
                  await QueryFactory.HL7MappingClient.patch(props.mappingId, {
                    rules: allRows,
                  });
                  await refetch();

                  ev.cancel = false;
                } catch (e) {
                  ev.cancel = true;
                  await modals.showError({ text: convertToErrorString(e) });
                } finally {
                  dataGridRef.current?.instance().endCustomLoading();
                }
              })();
            }}
            dataSource={dataSource}
            keyExpr={'id'}
            showBorders={true}
            style={{ width: '100%' }}
            onInitNewRow={(e) => {
              const data = e.data as Row;
              data.id = createId();
              if (isEmpty(e.data.enabled)) {
                data.enabled = true;
              }
              data.overwriteIfEmpty = e.data.overwriteIfEmpty ?? false;
              data.isMandatory = e.data.isMandatory ?? false;
              data.ignoreErrors = e.data.ignoreErrors ?? false;
            }}
            onEditingStart={(e) => {
              const row = e.data as Row;
              if (
                isPatientMandatoryField(row) &&
                e.column?.dataField !== nameof('expression')
              ) {
                e.cancel = true;
              }
              if (e.column?.dataField === nameof('defaultValue')) {
                if (!row.overwriteIfEmpty) {
                  e.cancel = true;
                }
              }
              if (
                e.column?.dataField === nameof('ignoreErrors') ||
                e.column?.dataField === nameof('overwriteIfEmpty')
              ) {
                if (row.isMandatory) {
                  // if field is mandatory then 'ignoreErrors' should be disabled
                  e.cancel = true;
                }
              }
              if (e.column?.dataField === nameof('overwriteIfEmpty')) {
                if (row.hasOverwriteSettings) {
                  // if field has overwrite settings then 'overwriteIfEmpty' should be disabled
                  e.cancel = true;
                }
              }
              if (row.isDefault) {
                if (e.column?.dataField === nameof('table')) {
                  e.cancel = true;
                }
                if (e.column?.dataField === nameof('dictionaryField')) {
                  e.cancel = true;
                }
                if (e.column?.dataField === nameof('fieldName')) {
                  e.cancel = true;
                }
              }
            }}
          >
            <DXG.Paging enabled={false} />
            <DXG.Editing
              mode="batch"
              allowUpdating={true}
              allowAdding={true}
              allowDeleting={true}
              selectTextOnEditStart={true}
              startEditAction="click"
              newRowPosition="pageTop"
            />
            {/*<SearchPanel visible={true} width={240} placeholder="Search..." />*/}
            <DXG.Sorting mode="single" />
            <DXG.Column dataField="id" sortOrder="desc" visible={false} />
            <DXG.Column
              width={'25%'}
              dataField={nameof('table')}
              dataType="string"
              caption={i18n.t('edit.table')}
              sortIndex={0}
              defaultSortOrder="asc"
              setCellValue={onTableChange}
            >
              <DXG.Lookup dataSource={tableNames} />
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              width={'25%'}
              dataField={nameof('dictionaryField')}
              caption={i18n.t('edit.dictionary_field')}
              dataType="string"
              sortIndex={1}
              defaultSortOrder="asc"
              setCellValue={onDictionaryFieldChange}
            >
              <DXG.Lookup
                dataSource={dictionaryFieldsFilterFunc as any}
                displayExpr="fieldName"
                valueExpr="fieldName"
                allowClearing={true}
              />
            </DXG.Column>
            <DXG.Column
              width={'25%'}
              dataField={nameof('fieldName')}
              caption={i18n.t('edit.field_name')}
              dataType="string"
              sortIndex={1}
              defaultSortOrder="asc"
              setCellValue={onFieldNameChange}
            >
              <DXG.RequiredRule />
              <DXG.Lookup
                dataSource={fieldNamesFilterFunc as any}
                displayExpr="fieldName"
                valueExpr="fieldName"
              />
            </DXG.Column>
            <DXG.Column
              width={'30%'}
              dataField={nameof('expression')}
              caption={i18n.t('edit.expression')}
              dataType="string"
              editCellComponent={HL7ExpressionBuilder}
            >
              <DXG.RequiredRule />
              <DXG.AsyncRule
                message={i18n.t('edit.validation.wrong_expression')}
                validationCallback={validateExpression}
              />
            </DXG.Column>
            <DXG.Column
              width={120}
              dataField={nameof('isMandatory')}
              caption={i18n.t('edit.is_mandatory')}
              dataType="boolean"
              setCellValue={(rowData, value) => {
                rowData.isMandatory = value;
                if (value) {
                  rowData.ignoreErrors = false;
                  rowData.overwriteIfEmpty = false;
                }
              }}
            />
            <DXG.Column
              width={120}
              dataField={nameof('ignoreErrors')}
              caption={i18n.t('edit.ignore_errors')}
              dataType="boolean"
            />
            <DXG.Column
              width={170}
              dataField={nameof('overwriteIfEmpty')}
              caption={i18n.t('edit.overwrite_if_empty')}
              dataType="boolean"
            />
            <DXG.Column
              width={120}
              dataField={nameof('defaultValue')}
              caption={i18n.t('edit.default_value')}
              dataType="string"
            />
            <DXG.Column
              width={80}
              dataField={nameof('enabled')}
              caption={i18n.t('edit.enabled')}
              dataType="boolean"
            />
            <DXG.Column type="buttons">
              <DXG.Button name="delete" visible={isDeleteVisible as any} />
              <DXG.Button
                text={i18n.t('activate_button')}
                visible={isActivateVisible as any}
                onClick={onRowActivate as any}
              />
              <DXG.Button
                text={i18n.t('deactivate_button')}
                visible={isDeactivateVisible as any}
                onClick={onRowDeactivate as any}
              />
              {/*<DevExpressButton*/}
              {/*  name="delete"*/}
              {/*  visible={({*/}
              {/*    row: { data },*/}
              {/*  }: {*/}
              {/*    row: { data: MeasurementFieldListDto };*/}
              {/*  }) => {*/}
              {/*    return (*/}
              {/*      !data.isDefault && !data.isInMappings && !data.isInMeasurementUI*/}
              {/*    );*/}
              {/*  }}*/}
              {/*/>*/}
            </DXG.Column>
          </DXG.DataGrid>
        </AutoSizedTableWrapper>
      )}
    </Loading>
  );
};

const _hl7FieldNames: ExpressionBuilderField[] = [];
const HL7ExpressionBuilder = React.memo(function HL7ExpressionBuilder({
  data,
}: {
  data: any;
}) {
  if (_hl7FieldNames.length === 0) {
    HL7MessageSegments.forEach((segment) => {
      const subfields = HL7MessageSubfields[segment.subfields];
      const id = segment.id.replace('.', '_');
      _hl7FieldNames.push({
        caption: id + ' ' + segment.description,
        value: id,
        dataType: DataType.Text,
        score: 1,
      });
      if (!subfields) {
        if (segment.subfields !== 'var') {
          // it's known and ok that `var` has no subfields description
          console.log(
            `Subfields for type '${segment.subfields}' were not found`,
          );
        }
      } else {
        if (subfields.length > 0) {
          let i = 0;
          for (const subfield of subfields) {
            i++;
            _hl7FieldNames.push({
              caption: `${id}_${i} ${subfield.description}`,
              value: `${id}_${i}`,
              dataType: DataType.Text,
              score: i + 1,
            });
            if (subfield.subfields) {
              const items = HL7MessageSubfields[subfield.subfields];
              if (items && items.length > 0) {
                let c = 0;
                for (const item of items) {
                  c++;
                  _hl7FieldNames.push({
                    caption: `${id}_${i}_${c} ${item.description}`,
                    value: `${id}_${i}_${c}`,
                    dataType: DataType.Text,
                    score: c + 1,
                  });
                }
              }
            }
          }
        }
      }
    });
  }
  const intellisenseContent = [
    ...expressionFunctions.map((x) => `declare function ${x.signature}`),
    ..._hl7FieldNames.map((x) => `declare const ${x.value} = "${x.caption}"`),
  ].join('\n');
  return (
    <MonacoExpressionBuilder
      defaultExpressionValue={data.value}
      onExpressionChanged={(e: string) => data.setValue(e)}
      intellisenseContent={intellisenseContent}
      disableValidation={true}
      height={40}
    />
  );
});

function isDeleteVisible(row: TableRow) {
  const data = row.row.data;
  return !data.isDefault && !row.row.removed;
}

function isActivateVisible(row: TableRow) {
  const data = row.row.data;

  return data.isDefault && data.isDeleted;
}

const patientMandatoryFields: string[] = [
  nameOfPatient('firstName'),
  nameOfPatient('lastName'),
  nameOfPatient('dateOfBirth'),
  nameOfPatient('number'),
].map((field) => field.toLowerCase());

function isPatientMandatoryField(row: Row) {
  return (
    row.table === 'Patient' &&
    !row.dictionaryField &&
    row.fieldName &&
    patientMandatoryFields.includes(row.fieldName.toLowerCase())
  );
}

function isDeactivateVisible(row: TableRow) {
  const data = row.row.data;
  return data.isDefault && !data.isDeleted && !isPatientMandatoryField(data);
}
